import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/da24vblv7gurs51ef6s0';

interface FormState {
  status: 'idle' | 'loading' | 'success' | 'error';
  message: string;
}

export default function NewsletterSection() {
  const [form, setForm] = useState<FormState>({ status: 'idle', message: '' });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const data = new FormData(formEl);

    // 反垃圾蜜罐检测
    const honeypot = (data.get('contact_alt') as string)?.trim() ?? '';
    if (honeypot) {
      setForm({ status: 'success', message: '订阅成功！感谢你的关注，我们会第一时间推送更新提醒。' });
      return;
    }

    setForm({ status: 'loading', message: '' });

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'contact_alt') return;
      if (typeof value === 'string' && value.trim() !== '') payload.append(key, value);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (parsed ? responseText : '');
      const isSpam = /spam|form data is spam/i.test(
        serverMsg + ' ' + responseText,
      );

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        formEl.reset();
        setForm({ status: 'success', message: '订阅成功！感谢你的关注，我们会第一时间推送更新提醒。' });
      } else {
        setForm({
          status: 'error',
          message: serverMsg || '订阅失败，请稍后重试。',
        });
      }
    } catch {
      setForm({ status: 'error', message: '网络异常，请稍后重试。' });
    }
  };

  return (
    <section className="relative overflow-hidden bg-primary-500 py-14 md:py-16">
      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/70 to-accent-500/40"></div>
      <div className="relative mx-auto max-w-3xl px-4 text-center md:px-6">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50">
          <i className="ri-notification-3-line"></i>
          更新提醒
        </span>
        <h2 className="font-heading mt-4 text-2xl text-background-50 md:text-4xl">
          订阅更新通知，不错过任何一话
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-50/90 md:text-base">
          留下常用邮箱，当热门作品更新时第一时间提醒你，极速加载、翻页流畅，畅快追更。
        </p>

        <form
          id="newsletter-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="mx-auto mt-7 flex max-w-xl flex-col gap-3 sm:flex-row"
        >
          <input
            type="email"
            name="email"
            required
            placeholder="请输入你的常用邮箱"
            autoComplete="email"
            className="w-full flex-1 rounded-md border border-background-50/25 bg-background-50/95 px-4 py-3 text-sm text-foreground-950 placeholder-foreground-500 outline-none transition-colors focus:border-background-50 focus:ring-2 focus:ring-accent-400/50"
          />
          <input
            type="text"
            name="contact_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="contact-alt-field"
          />
          <button
            type="submit"
            disabled={form.status === 'loading'}
            className="whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-bold text-primary-600 transition-colors hover:bg-background-100 disabled:opacity-70 cursor-pointer"
          >
            {form.status === 'loading' ? '提交中…' : '立即订阅'}
          </button>
        </form>

        {form.status === 'success' && (
          <p className="mt-4 flex items-center justify-center gap-1.5 rounded-md bg-background-50/20 px-4 py-2.5 text-sm text-background-50">
            <i className="ri-checkbox-circle-fill"></i>
            {form.message}
          </p>
        )}
        {form.status === 'error' && (
          <p className="mt-4 flex items-center justify-center gap-1.5 rounded-md bg-accent-600/30 px-4 py-2.5 text-sm text-background-50">
            <i className="ri-error-warning-fill"></i>
            {form.message}
          </p>
        )}
      </div>
    </section>
  );
}